<template>
  <div class="google-map-container">
    <div class="map-header">
      <span class="map-title">
        <i class="fas fa-map-marker-alt"></i>
        Localisation
      </span>
      <div class="map-type-selector" v-if="mapLoaded">
        <button
          v-for="type in mapTypes"
          :key="type.id"
          :class="['map-type-btn', { active: currentMapType === type.id }]"
          @click="setMapType(type.id)"
          :aria-label="type.label"
          :aria-pressed="currentMapType === type.id"
        >
          <i :class="type.icon" aria-hidden="true"></i>
          <span class="btn-label">{{ type.label }}</span>
        </button>
      </div>
    </div>

    <!-- Avertissement pour adresse approximative -->
    <div v-if="approximateWarning && !loading && !error" class="map-warning">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ approximateWarning }}</span>
    </div>

    <div class="map-wrapper" :style="{ height: mapHeight + 'vh' }">
      <LoadingSpinner
        v-if="loading"
        :overlay="false"
        message="Chargement de la carte..."
      />

      <div v-else-if="error" class="map-error">
        <i class="fas fa-exclamation-triangle"></i>
        <span>{{ error }}</span>
        <small v-if="fullAddress">Adresse recherchée : {{ fullAddress }}</small>
      </div>

      <div
        ref="mapElement"
        class="map"
        :class="{ 'map-hidden': loading || error }"
      ></div>

      <div
        class="map-resize-handle"
        @mousedown="startResize"
      >
        <i class="fas fa-grip-lines"></i>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useGoogleMapsLoader } from '../../composables/useGoogleMapsLoader.js'
import LoadingSpinner from './LoadingSpinner.vue'

const props = defineProps({
  address: {
    type: String,
    default: ''
  },
  postalCode: {
    type: String,
    default: ''
  },
  city: {
    type: String,
    default: ''
  }
})

const mapTypes = [
  { id: 'roadmap', label: 'Plan', icon: 'fas fa-map' },
  { id: 'satellite', label: 'Satellite', icon: 'fas fa-satellite' },
  { id: 'terrain', label: 'Relief', icon: 'fas fa-mountain' },
  { id: 'hybrid', label: 'Hybride', icon: 'fas fa-layer-group' }
]

const { loadGoogleMapsScript } = useGoogleMapsLoader()

const STORAGE_KEY = 'google-map-height-vh'
const DEFAULT_HEIGHT = 35
const MIN_HEIGHT = 15
const MAX_HEIGHT = 80

const mapElement = ref(null)
const map = ref(null)
const marker = ref(null)
const loading = ref(true)
const error = ref(null)
const mapLoaded = ref(false)
const currentMapType = ref('roadmap')
const approximateWarning = ref(null)

const savedHeight = localStorage.getItem(STORAGE_KEY)
const mapHeight = ref(savedHeight ? parseFloat(savedHeight) : DEFAULT_HEIGHT)

const startResize = (e) => {
  e.preventDefault()
  const startY = e.clientY
  const startHeight = mapHeight.value

  const onMouseMove = (moveEvent) => {
    const deltaY = moveEvent.clientY - startY
    const deltaVh = (deltaY / window.innerHeight) * 100
    const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeight + deltaVh))
    mapHeight.value = Math.round(newHeight * 10) / 10
  }

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    localStorage.setItem(STORAGE_KEY, mapHeight.value.toString())
    if (map.value) {
      window.google.maps.event.trigger(map.value, 'resize')
    }
  }

  document.body.style.cursor = 'ns-resize'
  document.body.style.userSelect = 'none'
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

const fullAddress = computed(() => {
  const parts = []
  if (props.address) parts.push(props.address)
  if (props.postalCode) parts.push(props.postalCode)
  if (props.city) parts.push(props.city)
  return parts.join(', ')
})

const hasValidAddress = computed(() => {
  return props.address || props.city || props.postalCode
})

const initMap = async () => {
  if (!hasValidAddress.value) {
    loading.value = false
    error.value = 'Aucune adresse renseignée'
    return
  }

  loading.value = true
  error.value = null

  try {
    await loadGoogleMapsScript()

    if (!mapElement.value) return

    // Default center on France
    const defaultCenter = { lat: 46.603354, lng: 1.888334 }

    map.value = new window.google.maps.Map(mapElement.value, {
      center: defaultCenter,
      zoom: 6,
      mapTypeId: currentMapType.value,
      mapTypeControl: false,
      // Le petit bonhomme orange : on le lâche dans une rue pour s'y promener.
      streetViewControl: true,
      fullscreenControl: true,
      zoomControl: true
    })

    mapLoaded.value = true

    // Geocode the address
    await geocodeAddress()

  } catch (err) {
    console.error('Erreur initialisation carte:', err)
    error.value = 'Impossible de charger Google Maps'
    loading.value = false
  }
}

const geocodeAddress = async () => {
  if (!window.google?.maps?.Geocoder || !fullAddress.value) {
    loading.value = false
    return
  }

  const geocoder = new window.google.maps.Geocoder()
  approximateWarning.value = null

  try {
    const result = await new Promise((resolve, reject) => {
      geocoder.geocode(
        { address: fullAddress.value, region: 'fr' },
        (results, status) => {
          if (status === 'OK' && results?.[0]) {
            resolve(results[0])
          } else {
            reject(new Error(status))
          }
        }
      )
    })

    const location = result.geometry.location
    const locationType = result.geometry.location_type

    // Check if the address is approximate
    // ROOFTOP = precise address
    // RANGE_INTERPOLATED = interpolated between two precise points
    // GEOMETRIC_CENTER = center of a street/area
    // APPROXIMATE = approximate location (postal code, city, etc.)
    if (locationType === 'APPROXIMATE') {
      approximateWarning.value = "Seule la commune a été retrouvée : le repère pointe le centre-ville, pas l'adresse exacte."
    } else if (locationType === 'GEOMETRIC_CENTER') {
      approximateWarning.value = "La rue a été retrouvée, mais pas le numéro : le repère est approximatif."
    }

    // Une nouvelle adresse ramène à la carte : resté dans la rue, on
    // continuerait de regarder l'ancien lieu.
    leaveStreetView()
    map.value.setCenter(location)
    map.value.setZoom(locationType === 'ROOFTOP' || locationType === 'RANGE_INTERPOLATED' ? 17 : 14)

    // Remove old marker if exists
    if (marker.value) {
      marker.value.setMap(null)
    }

    // Create new marker
    marker.value = new window.google.maps.Marker({
      position: location,
      map: map.value,
      title: fullAddress.value,
      animation: window.google.maps.Animation.DROP
    })

    // Add info window with precision info
    const precisionText = locationType === 'ROOFTOP' ? 'Adresse precise' :
                          locationType === 'RANGE_INTERPOLATED' ? 'Adresse interpolee' :
                          locationType === 'GEOMETRIC_CENTER' ? 'Centre de rue' :
                          'Position approximative'

    const infoWindow = new window.google.maps.InfoWindow({
      content: `<div style="padding: 8px; max-width: 220px;">
        <strong>Lieu de formation</strong><br/>
        <span style="color: #666;">${fullAddress.value}</span>
        <br/><small style="color: ${locationType === 'ROOFTOP' ? '#28a745' : '#dc6803'};">${precisionText}</small>
      </div>`
    })

    marker.value.addListener('click', () => {
      infoWindow.open(map.value, marker.value)
    })

    loading.value = false
    error.value = null

  } catch (err) {
    console.error('Erreur geocodage:', err)
    loading.value = false
    error.value = "Adresse introuvable - veuillez vérifier et corriger l'adresse"
    approximateWarning.value = null
  }
}

const leaveStreetView = () => {
  map.value?.getStreetView()?.setVisible(false)
}

const setMapType = (type) => {
  currentMapType.value = type
  if (map.value) {
    // Dans la rue, le panorama recouvre la carte : changer de fond sans en
    // sortir ne montrerait rien, et le bouton paraîtrait mort.
    leaveStreetView()
    map.value.setMapTypeId(type)
  }
}

// Debounce timer for address changes
let debounceTimer = null

// Watch for address changes with debounce
watch(
  fullAddress,
  async (newAddress, oldAddress) => {
    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    // Skip if address hasn't really changed
    if (newAddress === oldAddress) return

    // Debounce to avoid too many geocoding requests while typing
    debounceTimer = setTimeout(async () => {
      if (!hasValidAddress.value) {
        error.value = 'Aucune adresse renseignée'
        approximateWarning.value = null
        loading.value = false
        if (marker.value) {
          marker.value.setMap(null)
          marker.value = null
        }
        return
      }

      // Reset error state when address changes
      error.value = null

      // If map is not initialized yet, initialize it
      if (!map.value) {
        await initMap()
      } else {
        // Map exists, just geocode the new address
        await geocodeAddress()
      }
    }, 500) // 500ms debounce
  },
  { immediate: false }
)

onMounted(() => {
  initMap()
})

onUnmounted(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
  if (marker.value) {
    marker.value.setMap(null)
  }
  map.value = null
  marker.value = null
})
</script>

<style scoped>
.google-map-container {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-background);
  /* La carte vit tantôt pleine page, tantôt dans une colonne étroite : c'est
     la largeur du conteneur, pas celle du viewport, qui dit la place
     disponible dans l'en-tête. */
  container-type: inline-size;
}

.map-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-background-secondary);
  border-bottom: 1px solid var(--color-border);
}

.map-title {
  font-weight: 600;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.map-title i {
  color: var(--color-primary);
}

.map-type-selector {
  display: flex;
  gap: var(--spacing-xs);
}

.map-type-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-background);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8rem;
}

.map-type-btn:hover {
  background: var(--color-background-hover);
  border-color: var(--color-primary-light);
}

.map-type-btn.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.map-type-btn .btn-label {
  display: none;
}

@media (min-width: 768px) {
  .map-type-btn .btn-label {
    display: inline;
  }

  /* Titre (~125px) + boutons libellés (~345px avec le padding) demandent
     ~485px : en dessous, les boutons priment et le titre s'efface — la carte
     juste dessous dit déjà de quoi il s'agit. */
  @container (max-width: 500px) {
    .map-title {
      display: none;
    }

    .map-header {
      justify-content: flex-end;
    }
  }

  /* Colonne au plus serré : même seuls, les libellés ne tiennent plus. */
  @container (max-width: 370px) {
    .map-type-btn .btn-label {
      display: none;
    }
  }
}

.map-wrapper {
  position: relative;
}

.map {
  width: 100%;
  height: 100%;
}

.map-hidden {
  visibility: hidden;
}

.map-error {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  background: var(--color-background-secondary);
  color: var(--color-warning-dark);
  text-align: center;
  padding: var(--spacing-md);
}

.map-error i {
  font-size: 2rem;
  color: var(--color-warning);
}

.map-error small {
  color: var(--color-text-secondary);
  font-style: italic;
  margin-top: var(--spacing-xs);
}

.map-warning {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-warning-lighter, #fff3cd);
  border-bottom: 1px solid var(--color-warning, #ffc107);
  color: var(--color-warning-dark, #856404);
  font-size: 0.875rem;
}

.map-warning i {
  color: var(--color-warning, #ffc107);
  flex-shrink: 0;
}

.map-resize-handle {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ns-resize;
  background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.05));
  transition: background 0.2s ease;
  z-index: 1;
}

.map-resize-handle:hover {
  background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.12));
}

.map-resize-handle i {
  font-size: 0.7rem;
  color: var(--color-text-secondary);
  opacity: 0.5;
  transition: opacity 0.2s ease;
}

.map-resize-handle:hover i {
  opacity: 1;
}
</style>
